import { defaultComparisonSchema } from './features/comparison';
import {
  getMeasureFeature,
  getMeasureFormatString,
  getTimestampFeature,
  getTimestampGranularity,
  MEASURE_COLUMN_NAME,
  TIMESTAMP_COLUMN_NAME,
} from './features/metricValue';
import { defaultThresholdFeature } from './features/threshold';
import { maxPrecision as calculateMaxPrecision } from './formatting/formatParser';
import { DataRow, DataTable, DateGranularity, dateTruncFromGranularity, EffectiveDefinition } from './models';
import { isNullOrUndefined } from './models/DataTable';
import { Duration } from './models/Duration';
import { Err, MetricsEngineError, Ok, Result } from './models/Result';
import { DateWindowFeature } from './features/window/models';
import { ALL_TIME_DATE_WINDOW, SpecialDateWindowType } from './features/window/types/allTimeDateWindow';
import {
  defaultDateWindow,
  DateWindow,
  DateWindowTypes,
  createDefinedStartDateWindow,
  createRelativeWindow,
  createLastNWindow,
} from './features/window';

// This structure must match what is returned by the metrics-edge-service.
// If the structure of DataPoints changes on the server, it must also be updated here.
// tslint:disable-next-line: max-line-length
// https://gitlab.tableausoftware.com/metrics/service/edge/metrics-edge-service/blob/master/src/main/kotlin/com/tableau/metrics/edge/grpc/p4/json/MetricDataPoints.kt#L10
export interface ServerDataPoint {
  measure?: number;
  timestamp?: string;
}

export type ServerDataPoints = ReadonlyArray<ServerDataPoint>;

export const parseDataPoints = (points?: ServerDataPoints, maxPrecision: number = 0.01): DataTable => {
  if (!points) {
    return [];
  }
  return points.map((x) => parseDataPoint(x, maxPrecision)).filter((point) => point !== undefined) as DataTable;
};

const parseDataPoint = (point: ServerDataPoint, maxPrecision: number): DataRow | undefined => {
  const timestamp = validDateOrUndefined(point.timestamp);
  if (!timestamp) {
    return undefined;
  }

  const measure = point.measure;

  if (measure === undefined || measure === null || typeof measure !== 'number') {
    return undefined;
  }

  const measureFactor = 1 / maxPrecision;

  return {
    measure: Math.round(measure * measureFactor) / measureFactor,
    timestamp,
  };
};

const timezoneOffsetRegex = /[+-]\d\d:?(\d\d)?$/;

// This function will preserve existing timezone offsets on dates, and otherwise
// will use a regex to deconstruct the `dateString` and use a JS local date constructor.
// `dateString` _must_ take the form of a ISO8601 DateTime string in "extended" form
// (with or without tz) in order to be correctly parsed.
// Ex: "2020-01-01T00:00:00" for 12 AM on January 1, 2020
const dateWithTheRightTimezone = (dateString: string): Date | undefined => {
  if (dateString.endsWith('Z')) {
    return new Date(dateString);
  }

  if (timezoneOffsetRegex.test(dateString)) {
    // There is already a timezone offset.
    return new Date(dateString);
  }

  if (isNaN(new Date(dateString).getTime())) {
    return undefined;
  }

  const [date, time] = dateString.split('T');
  const [year, month, day] = date?.split('-').map((x) => parseInt(x, 10));
  const [hour, minute, second] = time?.split(':').map((x) => parseFloat(x));

  const [s, ms] = second
    .toString()
    .split('.')
    .map((x) => parseInt(x, 10));

  return new Date(year, month - 1, day, hour, minute, s, ms ?? 0);
};

export const validDateOrUndefined = (dateString?: string): Date | undefined => {
  if (isNullOrUndefined(dateString)) {
    return undefined;
  }

  const result = dateWithTheRightTimezone(dateString);

  if (result === undefined || isNaN(result.getTime())) {
    return undefined;
  }

  return result;
};

export interface DataPointsApiResponse {
  // non-optional data because if the server returns null for data,
  // we return [] (see parseDataPoints)
  readonly data: DataTable;
  readonly definition: EffectiveDefinition;
  readonly dataRefreshedTimestamp: Date | undefined;
}

// Using `any` here because types coming off the wire could look like anything!
// tslint:disable-next-line: no-any
export const parseResponse = (response: any): Result<DataPointsApiResponse, MetricsEngineError> => {
  if (response === null || response === undefined) {
    return Err(new MetricsEngineError('Response must not be null or undefined'));
  }
  if (typeof response !== 'object' || Array.isArray(response)) {
    return Err(new MetricsEngineError('Response must be an object'));
  }

  return parseDefinition(response).map((definition) => ({
    data: parseDataPoints(response?.points, calculateMaxPrecision(getMeasureFormatString(definition))),
    definition,
    dataRefreshedTimestamp: validDateOrUndefined(response.dataRefreshedTimestamp),
  }));
};

export const defaultFeatures = {
  measure: { column: MEASURE_COLUMN_NAME },
  timestamp: { column: TIMESTAMP_COLUMN_NAME },
};

// tslint:disable-next-line: no-any
export function parseDefinition(response: any): Result<EffectiveDefinition, MetricsEngineError> {
  const schema = response.schema;
  const features = response.features;

  let actualSchema = { ...schema };
  let actualFeatures = { ...defaultFeatures, ...features };
  const timestampColumnRef = getTimestampFeature(actualFeatures);
  const measureColumnRef = getMeasureFeature(actualFeatures);
  const granularity = getTimestampGranularity({ schema: actualSchema, features: actualFeatures });

  if (features?.threshold === undefined) {
    actualSchema = { ...schema, ...defaultComparisonSchema(timestampColumnRef, measureColumnRef, granularity) };
    actualFeatures.threshold = { thresholds: [defaultThresholdFeature()] };
  }

  if (features?.dateWindow) {
    actualFeatures.dateWindow = parseDateWindowFeature(features?.dateWindow, granularity);
  }

  return Ok({
    schema: actualSchema,
    features: actualFeatures,
  });
}

// tslint:disable-next-line: no-any
const parseDateWindowFeature = (dateWindowFeatureResponse: any, granularity: DateGranularity): DateWindowFeature => {
  const parsedDateWindows = parseDateWindows(dateWindowFeatureResponse?.dateWindows).filter((item) => item.isOk());
  const dateWindows =
    parsedDateWindows.length > 0
      ? parsedDateWindows.map((dateWindow) => dateWindow.unwrap())
      : [defaultDateWindow(dateTruncFromGranularity(granularity))];
  return { dateWindows };
};

// tslint:disable-next-line: no-any
const parseDateWindows = (dateWindowListResponse: any): ReadonlyArray<Result<DateWindow, MetricsEngineError>> => {
  // tslint:disable-next-line: no-any
  return dateWindowListResponse.map((dateWindowResponse: any) => parseDateWindow(dateWindowResponse));
};

// tslint:disable-next-line: no-any
const parseDateWindow = (dateWindowResponse: any): Result<DateWindow, MetricsEngineError> => {
  switch (dateWindowResponse?.type) {
    case DateWindowTypes.LATEST_DATA: {
      return latestDataOrLastNWindow(dateWindowResponse);
    }
    case DateWindowTypes.DEFINED_START: {
      const date = validDateOrUndefined(dateWindowResponse?.startDate);
      return date
        ? Ok(createDefinedStartDateWindow(date) as DateWindow)
        : Err(new MetricsEngineError('Could not parse date'));
    }
    case SpecialDateWindowType.ALL_TIME: {
      return Ok(ALL_TIME_DATE_WINDOW as DateWindow);
    }
    default:
      return Err(new MetricsEngineError(`Could not parse date window type ${dateWindowResponse?.type}`));
  }
};

// tslint:disable-next-line: no-any
const latestDataOrLastNWindow = (dateWindowResponse: any): Result<DateWindow, MetricsEngineError> => {
  const granularity = dateWindowResponse?.duration?.granularity ?? DateGranularity.UNKNOWN;
  const numberOfUnits = dateWindowResponse?.duration?.numberOfUnits;
  if (granularity !== DateGranularity.UNKNOWN && numberOfUnits) {
    return Ok(
      createRelativeWindow({
        unit: granularity,
        numberOfUnits: numberOfUnits,
      } as Duration) as DateWindow
    );
  } else if (numberOfUnits) {
    return Ok(createLastNWindow(numberOfUnits) as DateWindow);
  }
  return Err(new MetricsEngineError('Could not parse latest data or last n date window'));
};
